import {
  AccountBalance,
  AttachMoney,
  Calculate,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { CreateForm } from 'lib/api/sgr/types';
import CustomButton from 'lib/designSystem/Button';
import InfoCard from 'lib/designSystem/InfoCard';
import PageHeader from 'lib/designSystem/PageHeader';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatCurrency, formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

type CalculationResult = Output;

interface Props {
  open: boolean;
  form: CreateForm;
  rateControls: RiskPremiumRates;
  isPending: boolean;
  onClose: () => void;
  handleConfirm: () => Promise<void>;
}

const ConfirmationDialog: React.FC<Props> = ({
  open,
  form,
  rateControls,
  isPending,
  onClose,
  handleConfirm,
}) => {
  const dispatch = useDispatch();
  const { data: dollar } = getDollarValue(dispatch);
  const { data: uf } = getUfValue(dispatch);
  const { calculate } = riskPremiumCalculator();
  const [originalCalc, setOriginalCalc] = useState<CalculationResult>();
  const [modifiedCalc, setModifiedCalc] = useState<CalculationResult>();
  const { data: creditLineData } = getCreditLineByProduct(
    dispatch,
    form.businessIdentifier,
    'sgr',
  );

  const { quotedAmount, quotedCurrency, startDate, endDate, type } = form;

  const originalRates = useMemo(() => {
    const subProduct = creditLineData?.subProducts?.find(
      (sp) => sp.subProduct.id === type,
    );

    return {
      variableRate: subProduct?.operationCostVariable ?? null,
      fixedRate: subProduct?.operationCostFixed ?? null,
      variableDiscount: subProduct?.discountRateVariable ?? null,
      fixedDiscount: subProduct?.discountRateFixed ?? null,
    };
  }, [type]);

  useEffect(() => {
    const performCalculations = () => {
      try {
        const original = calculate({
          quotedAmount,
          startDate,
          endDate,
          quotedCurrency,
          rates: originalRates,
        });

        const modified = calculate({
          quotedAmount,
          startDate,
          endDate,
          quotedCurrency,
          rates: rateControls,
        });

        setOriginalCalc(original);
        setModifiedCalc(modified);
      } catch (error) {
        setOriginalCalc(undefined);
        setModifiedCalc(undefined);
      }
    };

    performCalculations();
  }, [quotedAmount, startDate, endDate, quotedCurrency, type, rateControls]);

  const originalPremium = originalCalc?.amount || 0;
  const modifiedPremium = modifiedCalc?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;
  const hasRateChanges = premiumDifference !== 0;

  const amountFormatted = formatCurrency(
    quotedAmount,
    quotedCurrency as 'CLP' | 'USD' | 'UF',
  );
  const conversionRate = quotedCurrency === 'UF' ? uf?.value : dollar?.value;
  const amountInCLP = quotedAmount * (conversionRate ?? 1);
  const showCLPBreakdown = quotedCurrency === 'UF' || quotedCurrency === 'USD';
  const warrantyAmountValue =
    amountFormatted +
    (showCLPBreakdown ? ` ≈ ${formatMoney(amountInCLP)} CLP` : '');
  const variableRateChange = `${originalRates.variableRate?.toFixed(2)}% → ${rateControls.variableRate?.toFixed(2)}%`;
  const fixedRateChange = `${originalRates.fixedRate?.toFixed(2)} UF → ${rateControls.fixedRate?.toFixed(2)} UF`;
  const variableDiscountChange = `${originalRates.variableDiscount?.toFixed(2)}% → ${rateControls.variableDiscount?.toFixed(2)}%`;
  const fixedDiscountChange = `${originalRates.fixedDiscount?.toFixed(2)}% → ${rateControls.fixedDiscount?.toFixed(2)}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <PageHeader.TitleOnly
          title="Confirmación de Creación de Garantía"
          iconType="calculate"
          subtitle={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
        />
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Grid container>
            <Grid item xs={3}>
              <InfoCard
                icon={<AttachMoney />}
                label="Monto de Garantía"
                value={warrantyAmountValue}
                color="blue"
              />
            </Grid>

            <Grid item xs={3}>
              <InfoCard
                icon={<Calculate />}
                label="Costo de emisión"
                value={`${formatMoney(modifiedPremium)} CLP`}
                color="green"
              />
            </Grid>

            {hasRateChanges && (
              <Grid item xs={3}>
                <InfoCard
                  icon={
                    premiumDifference > 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )
                  }
                  label="Variación"
                  value={`${premiumDifference > 0 ? '+' : ''}${formatMoney(premiumDifference)} CLP`}
                  color={premiumDifference > 0 ? 'green' : 'red'}
                />
              </Grid>
            )}

            <Grid item xs={3}>
              <InfoCard
                icon={<AccountBalance />}
                label="Fogape"
                value={form.fogape ? `Si / ${form.fogape}%` : 'No / -'}
                color="orange"
              />
            </Grid>
          </Grid>

          {hasRateChanges && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>
              </Grid>
              {rateControls.variableRate !== originalRates.variableRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Variable"
                    value={variableRateChange}
                    color={
                      (rateControls.variableRate ?? 0) >
                      (originalRates.variableRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedRate !== originalRates.fixedRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Fija"
                    value={fixedRateChange}
                    color={
                      (rateControls.fixedRate ?? 0) >
                      (originalRates.fixedRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.variableDiscount !==
                originalRates.variableDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Variable"
                    value={variableDiscountChange}
                    color={
                      (rateControls.variableDiscount ?? 0) >
                      (originalRates.variableDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Fijo"
                    value={fixedDiscountChange}
                    color={
                      (rateControls.fixedDiscount ?? 0) >
                      (originalRates.fixedDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Stack>

        {hasRateChanges && (
          <Alert
            severity="warning"
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: 4,
              borderRadius: 0.5,
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              py: 0.75,
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Tasas Modificadas
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Las tasas han sido modificadas respecto a la cotización original.
              El costo de emisión ha variado en{' '}
              {formatMoney(Math.abs(premiumDifference))} CLP (
              {premiumDifference > 0 ? 'incremento' : 'reducción'}).
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          loading={isPending}
          disabled={isPending}
        >
          Confirmar y Crear Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
